/**
 * Spanish number formatting. Period thousands, comma decimals, and the currency
 * code always BEFORE the figure: `USD 4.150`, never `$4.150` or `4.150 USD`.
 *
 * The currency code is not decoration. There is no single platform currency —
 * a Uruguayan brand may pay an Argentine creator — so every amount on screen
 * carries the code it was denominated in, and two figures in different
 * currencies are never summed.
 */

const ES = "es-UY";

/** `4150` → `4.150`. Decimals only when the amount actually has them. */
export function formatAmount(amount: number, opts?: { decimals?: boolean }): string {
  const wantsDecimals = opts?.decimals ?? !Number.isInteger(amount);
  return new Intl.NumberFormat(ES, {
    minimumFractionDigits: wantsDecimals ? 2 : 0,
    maximumFractionDigits: wantsDecimals ? 2 : 0,
  }).format(amount);
}

/** `4150, "USD"` → `USD 4.150`. */
export function formatMoney(
  amount: number | null | undefined,
  currency: string | null | undefined,
  opts?: { decimals?: boolean },
): string | null {
  if (amount === null || amount === undefined) return null;
  const code = currency?.trim() || "USD";
  return `${code} ${formatAmount(amount, opts)}`;
}

/** `2.1` → `2,1%`. Takes a percentage, not a ratio. */
export function formatPercent(value: number, decimals = 1): string {
  return `${new Intl.NumberFormat(ES, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value)}%`;
}

/**
 * `12400` → `12,4 K`. For follower counts, where the exact number is noise and
 * the order of magnitude is the point.
 */
export function formatCompact(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return null;
  if (value < 1000) return String(value);
  if (value < 1_000_000) {
    return `${new Intl.NumberFormat(ES, { maximumFractionDigits: 1 }).format(value / 1000)} K`;
  }
  return `${new Intl.NumberFormat(ES, { maximumFractionDigits: 1 }).format(value / 1_000_000)} M`;
}
